import { isPlainObject, mapValues, truncate } from 'lodash';
import isString from 'lodash/isString';
import pino from 'pino';
import stream from 'stream';

import { isDevelopmentEnvironment } from '~/main/environments';
import Session from '~/main/Session';
import { addBreadcrumb, Severity } from '~/main/utils/addBreadcrumb';
import { captureExceptionRateLimit } from '~/main/utils/exception-rate-limit';
import { FileLogger } from '~/main/utils/file-logger';
import { pathScrubber } from '~/main/utils/path-scrubber';
import { Transport } from '~/renderer/hooks/socket/types';
import { coerceError, isExpectedError } from '~/shared';

export enum LoggerScope {
  PRINTER = 'PRINTER',
  APP = 'APP',
  CFD = 'CFD',
  UPDATER = 'UPDATER',
  ELECTRON = 'Electron',
  HUB = 'Hub',
  ABLY = 'Ably',
  ABLY_CUSTOMER = 'AblyCustomer',
  APPCUES = 'Appcues',
  ANALYTICS = 'Analytics',
  TIME = 'Time',
  LOCAL_SERVER = 'LocalServer',
}

export interface LoggerParams {
  scope: LoggerScope;
  transport?: Transport;
}

export interface LogParams {
  msg?: string;
  err?: Error | unknown;
  [key: string]: unknown;
}

export interface ErrorLogParams extends LogParams {
  err: Error | unknown;
}

type LogFn = (args: LogParams) => void;
type ErrorFn = (args: ErrorLogParams) => void;

export interface Logger {
  debug: LogFn;
  error: ErrorFn;
  info: LogFn;
  warn: LogFn;
}

const getBreadcrumbInfo = (arg: { msg?: string; err?: Error | unknown } | string) => {
  if (typeof arg === 'string') {
    return { message: arg };
  }
  const { msg: message, ...data } = arg || {};

  return { message, data };
};

let loggerInstance: Logger | undefined;

// Create a passthrough stream so pino doesn't try to wrap stdout in SonicBoom which causes an ebadf crash on windows only
const shouldWorkaround = process.platform === 'win32' && process.type;
let outStream: stream.Writable = process.stdout;
if (shouldWorkaround) {
  outStream = new stream.PassThrough();
  outStream.pipe(process.stdout);
}

const getLogger = (): Logger => {
  if (loggerInstance) {
    return loggerInstance;
  }
  const { isFileLoggerEnabled } = Session.store?.getState?.()?.hub || {};
  if (isFileLoggerEnabled) {
    outStream = FileLogger.getWriteStream();
  }

  loggerInstance = pino(
    {
      level: 'trace',
      prettyPrint: {
        colorize: true,
        translateTime: 'SYS:standard',
        messageFormat: '{scope} - {msg}',
        ignore: 'scope,hostname,pid',
      },
      browser: {
        asObject: true,
        serialize: ['!stdSerializers.err'],
      },
      redact: !isDevelopmentEnvironment()
        ? {
            paths: ['err.stack'],
            censor: pathScrubber,
          }
        : undefined,
    },
    outStream,
  );

  return loggerInstance!;
};

const recursiveTruncate = (obj: { [key: string]: unknown }): { [key: string]: unknown } => {
  if (isPlainObject(obj)) {
    return mapValues(obj, recursiveTruncate);
  }
  if (isString(obj)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return truncate(obj, { length: 160 }) as any;
  }
  return obj;
};

const cleanLoggersCache = (): void => {
  loggerInstance = undefined;
};

const logger = ({ scope }: LoggerParams): Logger => {
  const { enabledLoggerScopes } = Session.store?.getState?.()?.hub || {};

  if (enabledLoggerScopes === 'none') return dummyLogger({ scope });
  if (Array.isArray(enabledLoggerScopes) && !enabledLoggerScopes?.includes(scope))
    return dummyLogger({ scope });

  return {
    debug: args => {
      addBreadcrumb({
        level: Severity.Debug,
        type: 'debug',
        ...getBreadcrumbInfo(args),
        category: 'logger',
      });

      getLogger().debug({ scope, ...recursiveTruncate(args) });
    },
    error: args => {
      getLogger().error({
        scope,
        ...args,
        err: isExpectedError(args.err) ? args.err.message : args.err,
      });

      const { err: error, ...rest } = args;

      const err = coerceError(error || rest?.msg || args);

      if (!isExpectedError(err)) {
        captureExceptionRateLimit(err, isString(args) ? undefined : { extra: rest });
      }
    },
    info: args => {
      addBreadcrumb({
        level: Severity.Info,
        type: 'debug',
        ...getBreadcrumbInfo(args),
        category: 'logger',
      });

      getLogger().info({ scope, ...recursiveTruncate(args) });
    },
    warn: args => {
      addBreadcrumb({
        level: Severity.Warning,
        type: 'debug',
        ...getBreadcrumbInfo(args),
        category: 'logger',
      });

      getLogger().warn({
        scope,
        ...args,
        err: isExpectedError(args.err) ? args.err.message : args.err,
      });
    },
  };
};

function dummyLogger({ scope }: LoggerParams) {
  const pinoLogger = pino({
    name: scope,
    level: 'trace',
    prettyPrint: {
      colorize: true,
      translateTime: 'SYS:standard',
    },
    browser: {
      asObject: true,
      serialize: ['!stdSerializers.err'],
    },
  });

  return {
    debug: pinoLogger.debug.bind(pinoLogger),
    error: pinoLogger.error.bind(pinoLogger),
    info: pinoLogger.info.bind(pinoLogger),
    warn: pinoLogger.warn.bind(pinoLogger),
  };
}

export { logger, cleanLoggersCache };
